import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/app/layouts/AppLayout';
import { RedirectIfAuthenticated, RequireAuth, RequireRole } from '@/app/guards';
import { LoginPage } from '@/features/auth/LoginPage';
import { NotFoundPage } from '@/app/NotFoundPage';
import { Role } from '@/types/domain';
import { lazyRoute } from '@/app/lazyRoute';
import { RouteError } from '@/app/RouteError';

// -----------------------------------------------------------------------------
// Routes.
//
// Every screen is code-split. Recharts alone is ~180KB and only the dashboard
// needs it; a staff member who opens the panel to clear the review queue should
// not download a charting library to do it.
//
// The sign-in route is NOT lazy: it is the first thing an unauthenticated user
// sees, and a spinner before the login form is a spinner before anything useful.
// -----------------------------------------------------------------------------

const DashboardPage = lazyRoute(() => import('@/features/dashboard/DashboardPage'));
const ApplicationsPage = lazyRoute(() => import('@/features/applications/ApplicationsPage'));
const ReviewQueuePage = lazyRoute(() => import('@/features/moderation/ReviewQueuePage'));
const MembersPage = lazyRoute(() => import('@/features/people/PeoplePage'));
const CoachesPage = lazyRoute(() => import('@/features/people/CoachesPage'));
const StaffPage = lazyRoute(() => import('@/features/staff/StaffPage'));
const DeletionsPage = lazyRoute(() => import('@/features/people/DeletionsPage'));
const PlansPage = lazyRoute(() => import('@/features/commerce/PlansPage'));
const CouponsPage = lazyRoute(() => import('@/features/commerce/CouponsPage'));
const SubscriptionsPage = lazyRoute(() => import('@/features/commerce/SubscriptionsPage'));
const PaymentsPage = lazyRoute(() => import('@/features/commerce/PaymentsPage'));
const MembershipsPage = lazyRoute(() => import('@/features/memberships/MembershipsPage'));
const CoachAssignmentsPage = lazyRoute(() => import('@/features/memberships/CoachAssignmentsPage'));
const BillingAnomaliesPage = lazyRoute(() => import('@/features/memberships/BillingAnomaliesPage'));
const TicketsPage = lazyRoute(() => import('@/features/support/TicketsPage'));
const PushComposerPage = lazyRoute(() => import('@/features/messaging/PushComposerPage'));
const EmailComposerPage = lazyRoute(() => import('@/features/messaging/EmailComposerPage'));
const CampaignHistoryPage = lazyRoute(() => import('@/features/messaging/CampaignHistoryPage'));
const ConfigurationPage = lazyRoute(() => import('@/features/system/ConfigurationPage'));
const AuditLogPage = lazyRoute(() => import('@/features/system/AuditLogPage'));
const WalletOverviewPage = lazyRoute(() => import('@/features/wallet/WalletOverviewPage'));
const WalletMembersPage = lazyRoute(() => import('@/features/wallet/WalletMembersPage'));
const WalletTransactionsPage = lazyRoute(() => import('@/features/wallet/WalletTransactionsPage'));
const EconomySettingsPage = lazyRoute(() => import('@/features/wallet/EconomySettingsPage'));

export const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: (
      <RedirectIfAuthenticated>
        <LoginPage />
      </RedirectIfAuthenticated>
    ),
  },

  // There is deliberately no /sign-up route. Staff accounts are created by a
  // super admin from the Staff screen; a self-service path into an admin panel
  // is a path an attacker can walk too.

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            // Pathless, so it adds no URL segment: it exists to give every
            // screen an error element that renders INSIDE the shell.
            errorElement: <RouteError />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'applications', element: <ApplicationsPage /> },
              { path: 'review-queue', element: <ReviewQueuePage /> },
              { path: 'members', element: <MembersPage /> },
              { path: 'coaches', element: <CoachesPage /> },
              // ADMIN-tier to VIEW, because settling a wallet balance before an
              // account disappears is ordinary support work. The destructive action
              // ON the page (erase early) is SUPER_ADMIN, enforced server-side by
              // the route guard on `POST /admin/deletions/:id/purge` - a client
              // guard would only hide the button.
              { path: 'deletions', element: <DeletionsPage /> },
              // Reading the catalogue is ADMIN; the PATCH behind "Save price" is
              // SUPER_ADMIN and enforced on the API. The page renders read-only for
              // an admin rather than hiding - looking up what a plan costs is
              // ordinary support work.
              { path: 'plans', element: <PlansPage /> },
              // Reading is ADMIN; the POST/PATCH behind the buttons are SUPER_ADMIN
              // and enforced on the API. Same treatment as plan pricing.
              { path: 'coupons', element: <CouponsPage /> },
              { path: 'subscriptions', element: <SubscriptionsPage /> },
              { path: 'payments', element: <PaymentsPage /> },

              // Sect.fit memberships (Advanced / Pro). ADMIN to view and to issue
              // commands; the kill switch and pricing are SUPER_ADMIN on the API.
              { path: 'memberships', element: <MembershipsPage /> },
              { path: 'memberships/coach-assignments', element: <CoachAssignmentsPage /> },
              { path: 'memberships/anomalies', element: <BillingAnomaliesPage /> },
              // The plan catalogue is now a tab ON Memberships; the old address
              // still lands there for anyone with it bookmarked.
              { path: 'memberships/settings', element: <Navigate to="/memberships?view=plans" replace /> },

              // Sect Coins. ADMIN to view and to correct a member's balance (audited
              // on the API); the economy SAVE is SUPER_ADMIN on the API, and the
              // settings page renders read-only for an admin rather than hiding.
              { path: 'wallet', element: <WalletOverviewPage /> },
              { path: 'wallet/members', element: <WalletMembersPage /> },
              { path: 'wallet/transactions', element: <WalletTransactionsPage /> },
              { path: 'wallet/settings', element: <EconomySettingsPage /> },
              { path: 'tickets', element: <TicketsPage /> },

              // Composing and previewing is ADMIN work; the SEND is gated
              // SUPER_ADMIN on the API, and the composer shows that plainly rather
              // than hiding the screen - an admin drafting an announcement for a
              // super admin to send is a real workflow.
              { path: 'messaging/push', element: <PushComposerPage /> },
              { path: 'messaging/email', element: <EmailComposerPage /> },
              { path: 'messaging/history', element: <CampaignHistoryPage /> },

              // Rank-based, so SUPER_ADMIN passing an ADMIN gate needs no second
              // branch anywhere. These three are the super-admin-only surface.
              {
                path: 'staff',
                element: (
                  <RequireRole minimum={Role.SUPER_ADMIN}>
                    <StaffPage />
                  </RequireRole>
                ),
              },
              {
                path: 'configuration',
                element: (
                  <RequireRole minimum={Role.SUPER_ADMIN}>
                    <ConfigurationPage />
                  </RequireRole>
                ),
              },
              {
                path: 'audit-log',
                element: (
                  <RequireRole minimum={Role.SUPER_ADMIN}>
                    <AuditLogPage />
                  </RequireRole>
                ),
              },

              { path: '404', element: <NotFoundPage /> },
              { path: '*', element: <Navigate to="/404" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
